import { useEffect, useMemo, useState } from 'react'
import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import {
  GlobalSearchStateActions,
  GLOBAL_SEARCH_INITIAL_STATE,
} from 'contexts/GlobalSearch/GlobalSearchState'
import { getAirportTransferSearchStateFromParams } from 'lib/airportTransfer/airportTransferUtils'

export const GLOBAL_AIRPORT_TRANSFER_SEARCH_INITIAL_STATE: GlobalSearchState = {
  ...GLOBAL_SEARCH_INITIAL_STATE,
  pickUpTime: '09:00',
  dropOffTime: '09:00',
  airportTransferJourneyType: 'one-way',
  airportTransferPassengers: 2,
  airportTransferLuggage: 2,
}

export function useGlobalAirportTransferSearchContext(
  initialState: GlobalSearchState = GLOBAL_AIRPORT_TRANSFER_SEARCH_INITIAL_STATE,
  syncWithURLSearchParams?: URLSearchParams,
) {
  const [isSearchStateHydrated, setIsSearchStateHydrated] = useState(!syncWithURLSearchParams)
  const urlState = useMemo(() => {
    if (syncWithURLSearchParams) {
      return getAirportTransferSearchStateFromParams(syncWithURLSearchParams)
    }

    return undefined
  }, [syncWithURLSearchParams])
  const { globalSearchState, globalSearchDispatch } = useGlobalSearchContext(initialState)

  useEffect(() => {
    if (!urlState) {
      setIsSearchStateHydrated(true)
      return
    }

    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_JOURNEY_TYPE,
      journeyType:
        urlState.airportTransferJourneyType ??
        GLOBAL_AIRPORT_TRANSFER_SEARCH_INITIAL_STATE.airportTransferJourneyType,
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_LUGGAGE,
      luggage: urlState.airportTransferLuggage ?? 0,
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_AIRPORT_TRANSFER_PASSENGERS,
      passengers: urlState.airportTransferPassengers ?? 1,
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_CHECKIN_DATE,
      date: urlState.checkinDate,
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
      date: urlState.checkoutDate,
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_RETURN_TIME,
      dropOffTime: urlState.dropOffTime ?? '',
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_PICKUP_TIME,
      pickupTime: urlState.pickUpTime ?? '',
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: urlState.searchItem,
    })
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_SECONDARY_SEARCH_ITEM,
      searchItem: urlState.secondarySearchItem,
    })
    setIsSearchStateHydrated(true)
  }, [globalSearchDispatch, urlState])

  return {
    globalAirportTransferSearchDispatch: globalSearchDispatch,
    globalAirportTransferSearchState: globalSearchState,
    isSearchStateHydrated,
  }
}
